import React, { useState } from "react";
import Utils from "../../utility";
import { validateDiscountCode } from "../../services/userService";

const DiscountCodeInput = ({ onDiscountApplied, planId, disabled = false }) => {
  const [discountCode, setDiscountCode] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [error, setError] = useState("");

  const handleValidateDiscount = async () => {
    if (!discountCode || discountCode.trim() === "") {
      setError("Please enter a discount code");
      return;
    }

    setIsValidating(true);
    setError("");

    try {
      const response = await validateDiscountCode(discountCode, planId);

      if (response?.responseCode === 200 && response?.responseData?.valid) {
        const discount = response.responseData.discount;
        setAppliedDiscount(discount);
        setError("");

        Utils.successToastMessage(
          `Discount code applied! ${discount.description}`
        );

        if (onDiscountApplied) {
          onDiscountApplied(discount);
        }
      } else {
        const message =
          response?.responseData?.message ||
          response?.responseMessage ||
          "Invalid discount code";
        setError(message);
        setAppliedDiscount(null);
        Utils.failureToastMessage(message);
      }
    } catch (err) {
      // Backend (HTTPHandler.response) puts the real reason in `message`; keep
      // `responseMessage` as a fallback for any endpoint that uses it.
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.responseMessage ||
        "Error validating discount code";
      setError(message);
      setAppliedDiscount(null);
      Utils.failureToastMessage(message);
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveDiscount = () => {
    setDiscountCode("");
    setAppliedDiscount(null);
    setError("");
    if (onDiscountApplied) {
      onDiscountApplied(null);
    }
  };

  return (
    <div className="mt-4 w-full">
      <div className="flex flex-col gap-2">
        <label className="font-InterMedium text-ft14-19.6 text-grey-24">
          Have a discount code?
        </label>

        {!appliedDiscount ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
              placeholder="Enter code"
              disabled={disabled || isValidating}
              className="h-10 flex-1 rounded-lg border border-grey-8 px-3 font-InterRegular text-ft14-19.6 text-grey-24 focus:border-green-12 focus:outline-none disabled:cursor-not-allowed disabled:bg-grey-2"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleValidateDiscount();
                }
              }}
            />
            <button
              onClick={handleValidateDiscount}
              disabled={disabled || isValidating || !discountCode.trim()}
              className="h-10 rounded-lg bg-green-12 px-6 font-InterBold text-ft14-19.6 text-white transition-colors hover:bg-green-14 disabled:cursor-not-allowed disabled:bg-grey-8"
            >
              {isValidating ? "Validating..." : "Apply"}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between rounded-lg border border-green-12 bg-green-1 p-3">
            <div className="flex flex-col">
              <span className="font-InterBold text-ft14-19.6 text-green-12">
                {appliedDiscount.code} Applied!
              </span>
              <span className="font-InterRegular text-ft12-16.8 text-grey-14">
                {appliedDiscount.description}
              </span>
            </div>
            <button
              onClick={handleRemoveDiscount}
              disabled={disabled}
              className="font-InterMedium text-ft12-16.8 text-red-500 transition-colors hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Remove
            </button>
          </div>
        )}

        {error && !appliedDiscount && (
          <span className="font-InterRegular text-ft12-16.8 text-red-500">
            {error}
          </span>
        )}
      </div>
    </div>
  );
};

export default DiscountCodeInput;
